

import { BreadcrumbGroup, Button, FormField, Header, Link, Modal, SpaceBetween, Table } from '@cloudscape-design/components';
import { mdiAlertOctagon, mdiAlertRhombus, mdiBellOutline, mdiCalendarArrowLeft, mdiCircleSmall, mdiFileExcel, mdiTimerOutline, mdiTowTruck } from '@mdi/js';
import Icon from '@mdi/react';
import { addMonths, addYears, differenceInDays, endOfMonth, format, parse, startOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from "xlsx";
import Favorites from '../../../../../components/extras/Favorites';
import MPModal from '../../../../../components/MPModal';
import { fetchWithWatch } from '../../../../../hooks/fetchWithWatch';
import { btnIconForm, ItemFlight } from '../../../../../resources/types';
import { addNotification, addNotificationStatus, buildBreadcrumbItems, getCookie, getUrlVersion, minutesToHHMM2, setData, setLoading, setParameters } from '../../../../commons/functions';
import { useGlobalState } from '../../../../context/GlobalStateContext';
import { CustomAppLayout } from '../../../layout';

type TableItem = {
  displayName: React.ReactNode;
  Total: number | React.ReactNode;
  [key: string]: number | React.ReactNode; // allow month keys and React nodes
};

interface ReportItem {
  idUser: number;
  displayName: string;
  [key: string]: any; // This allows F_2026_01, C_2026_01, etc.
}

const generateMonthRange = (endDate: Date) => {
  return Array.from({ length: 12 }, (_, i) => {
    const d = subMonths(startOfMonth(endDate), 11 - i);

    return {
      id: format(d, "yyyy_MM"),
      label: format(d, "MM/yy", { locale: es })
    };
  });
};
export const ReportsEDVTowingsPage = () => {
  const { menu, parameters } = useGlobalState();
  const tableRef = useRef(null);

  const [report, setReport] = useState<ReportItem[]>([]);
  const [flights, setFlights] = useState<ItemFlight[]>();
  const [filter, setFilter] = useState<any>();

  const reportEndDate = (() => {
    if (parameters.period && parameters.period !== "0") {
      // 1. Parse '2026-04' into a Date object (defaults to the 1st of that month)
      const monthStart = parse(parameters.period, "yyyy-MM", new Date());

      // 2. Automatically get the correct last day (30th, 31st, or 28th/29th)
      return endOfMonth(monthStart);
    }

    return new Date();
  })();
  const monthRange = useMemo(() => generateMonthRange(reportEndDate), [reportEndDate]);
  const handleRefreshReport = async () => {
    if (parameters.year) {
      try {
        setReport([])
        setLoading(true)
        const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-routing': '/private/reports/edv/towings',
            'JWT': getCookie("JWT"),
            //  
          },
          body: JSON.stringify({
            until: format(reportEndDate, "dd-MM-yyyy")
          }),
          //mode: 'no-cors'  // Bypasses CORS but limits the response
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const data = await response.json();
        addNotificationStatus(data, setData);
        if (data.code == 0) {
          const transformed = data.report.map((item: any) => {
            const totalTime = monthRange.reduce((sum, m) => sum + Number(item[`F_${m.id}`] || 0), 0);
            const totalFlights = monthRange.reduce((sum, m) => sum + Number(item[`C_${m.id}`] || 0), 0);

            // 1. Create the base dynamic data first
            const dynamicData: any = {};
            monthRange.forEach(m => {
              dynamicData[`V${m.id}`] = Number(item[`F_${m.id}`] || 0);
              dynamicData[`T${m.id}`] = Number(item[`C_${m.id}`] || 0);
            });

            // 2. Combine them so totals are physically at the end of the object
            return {
              displayName: item.displayName,
              idUser: item.idUser,
              ...dynamicData, // Spreads all V_ID and T_ID keys here
              totalTime: Number(totalTime.toFixed(2)),
              totalFlights: totalFlights,
              daysLastFlight: item.daysLastFlight
            };
          });

          setReport(transformed);
        }
      } catch (error: any) {
        addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setData);
      } finally { setLoading(false) }

    };
  }
  const handleGetFlights = async (flt: any) => {
    try {
      setLoading(true)
      const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-routing': '/private/global/flights/list',
          'JWT': getCookie("JWT"),
          //  
        },
        body: JSON.stringify(flt),
        //mode: 'no-cors'  // Bypasses CORS but limits the response
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      const data = await response.json();
      addNotificationStatus(data, setData);
      if (data.code == 0) {

        setFlights(data.flights)
      }
    } catch (error: any) {
      addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setData);
    } finally { setLoading(false) }
  }

  const exportToExcel = () => {
    // Convert JSON to worksheet
    const ws = XLSX.utils.json_to_sheet(report);

    // Create a workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Movements");

    // Generate XLSX file and download
    XLSX.writeFile(wb, "report.towings.xlsx");
  };

  useEffect(() => {
    handleRefreshReport();
  }, [parameters.period]);

  const [sortingColumn, setSortingColumn] = useState<any>({ sortingField: "displayName" });
  const [isDescending, setIsDescending] = useState(false);
  const columns = useMemo(() => [
    {
      id: "displayName",
      header: "Remolcador / Vuelos",
      cell: (item: any) =>
        <FormField
          label={item.displayName}
          constraintText={item.daysLastFlight > 90 ? <><Icon path={mdiAlertOctagon} size={0.75} color='red' />Vencido</> :
            item.daysLastFlight > 75 ? <><Icon path={mdiAlertRhombus} size={0.75} color='orange' />Próximo </> : item.daysLastFlight > 60 ? <><Icon path={mdiBellOutline} size={0.75} color='grey' />Observación </> : undefined
          }
        />,
      sortingField: "displayName",
    },
    ...monthRange.map((m) => ({
      id: m.id,
      header: m.label.charAt(0).toUpperCase() + m.label.slice(1),
      minWidth: 80,
      width: 90,
      // The sorting happens on the raw flight time
      //sortingField: `V${m.id}`,
      cell: (item: any) => {
        const fVal = item[`V${m.id}`];
        const cVal = item[`T${m.id}`];

        if (fVal === 0) {
          return <Icon path={mdiCircleSmall} size={1} color='red' />;
        }

        return (
          <FormField
            label={`${fVal?.toFixed(2) || "0"}`}
            stretch
          ><Link onClick={(e) => {
            const flt = {
              idUserPilot: item.idUser,
              idFlightType: 18459,
              limit: 20,
              endDate: format(endOfMonth(parse(m.id, "yyyy_MM", new Date())), "dd-MM-yyyy"),
              startDate: format(startOfMonth(parse(m.id, "yyyy_MM", new Date())), "dd-MM-yyyy"),
              orderBy: "departureDate",
              order: "desc",
            }
            handleGetFlights(flt)
          }}>

              {cVal || 0}
            </Link>
          </FormField>
        );
      },
    })),
    {
      id: "time",
      header: <Icon path={mdiTimerOutline} size={1} />,
      cell: (item: any) => item.totalTime.toFixed(2),
      minWidth: 70,
      width: 80,
      sortingField: "time",
      sortingComparator: (a: any, b: any) => a.totalTime - b.totalTime,
    },
    {
      id: "flights",
      header: <Icon path={mdiTowTruck} size={1} />,
      cell: (item: any) => <Link onClick={(e) => {
        const flt = {
          idUserPilot: item.idUser,
          idFlightType: 18459,
          limit: 300,
          endDate: format(endOfMonth(new Date(parameters.year, parameters.month - 1, 1)), "dd-MM-yyyy"),
          startDate: format(addYears(startOfMonth(new Date(parameters.year, parameters.month - 1, 1)), -1), "dd-MM-yyyy"),
          orderBy: "departureDate",
          order: "desc",
        }
        handleGetFlights(flt)
      }}>{item.totalFlights}</Link>,
      minWidth: 50,
      width: 70,
      sortingField: "flights",
      sortingComparator: (a: any, b: any) => a.totalFlights - b.totalFlights
    },
    {
      id: "daysLastFlight",
      header: <Icon path={mdiCalendarArrowLeft} size={1} />,
      cell: (item: any) => item.daysLastFlight > 0 ? item.daysLastFlight : "------",
      minWidth: 50,
      width: 70,
      sortingField: "daysLastFlight",
      sortingComparator: (a: any, b: any) => a.daysLastFlight - b.daysLastFlight
    },
  ], [monthRange]);


  const sortedReport = useMemo(() => {
    const data = [...report];
    const { sortingField } = sortingColumn;

    const columnDefinition = columns.find(col => (col as any).sortingField === sortingField) as any;
    const comparator = columnDefinition?.sortingComparator;

    data.sort((a, b) => {
      let result = 0;
      if (comparator) {
        result = comparator(a, b);
      } else if (sortingField) {
        const valA = a[sortingField as keyof ReportItem];
        const valB = b[sortingField as keyof ReportItem];

        // Check if both values are numbers
        if (typeof valA === 'number' && typeof valB === 'number') {
          result = valA - valB;
        } else {
          // Fallback for strings
          const strA = (valA ?? "").toString().toLowerCase();
          const strB = (valB ?? "").toString().toLowerCase();
          result = strA.localeCompare(strB);
        }
      }
      return isDescending ? -result : result;
    });

    return data;
  }, [report, sortingColumn, isDescending, columns]);
  return (
    <CustomAppLayout
      toolsHide
      contentType="form"
      breadcrumbs={
        <BreadcrumbGroup
          items={buildBreadcrumbItems(menu)}
          expandAriaLabel="Show path"
          ariaLabel="Breadcrumbs"
        />
      }
      content={
        <>
          <SpaceBetween direction="vertical" size="xs">

            <Header actions={
              <SpaceBetween direction="horizontal" size="xs">

                <Favorites />
              </SpaceBetween>
            }
              description={"Consolidado de cantidad de remolques y suma de tiempos de vuelo en un periodo de un año"}
            >
              Reporte de remolques
            </Header>
            <Table
              columnDefinitions={columns}
              items={sortedReport} // Pass the SORTED list here
              sortingColumn={sortingColumn}
              sortingDescending={isDescending}
              onSortingChange={(e) => {
                setSortingColumn(e.detail.sortingColumn);
                setIsDescending(e.detail.isDescending);
              }}
              stickyHeader
              stickyColumns={{ first: 1, last: 3 }}

              filter={
                <SpaceBetween direction="horizontal" size="xs">
                  <div style={{ minWidth: 400 }}>
                    <SpaceBetween direction="horizontal" size="l">


                      <FormField label="Periodo de corte">

                        <div style={{ width: 130 }}>

                          <MPModal
                            width={120}
                            today prevMonth nextMonth
                            end={addMonths(new Date(), 1)}
                            date={`${parameters.period}`}
                            onConfirm={(date) => {
                              setParameters({ ...parameters, period: date.period, month: date.month, year: date.year });
                            }}
                          />
                        </div>
                      </FormField>
                      {report && report.length > 0 && (
                        <FormField label="&nbsp;">
                          <Button style={{ root: btnIconForm }} onClick={exportToExcel}><Icon path={mdiFileExcel} size={1} /></Button>
                        </FormField>
                      )}
                    </SpaceBetween>
                  </div>

                </SpaceBetween>
              }
              ref={tableRef}
              variant="embedded"
            />
            {!!flights && (
              <Modal size="max" visible={!!flights} onDismiss={() => setFlights(undefined)} closeAriaLabel="Close modal" header="Vuelos">
                <Table
                  items={flights}
                  stickyHeader
                  cellVerticalAlign='top'
                  variant='embedded'
                  footer=
                  {
                    flights.length > 0 && (<SpaceBetween direction="horizontal" size="xs">
                      <div>
                        Número de vuelos: <strong>{flights.length}</strong>
                      </div>
                      <div>
                        Tiempo total: <strong>{flights.reduce((acc, flight) => acc + (flight.delta ?? 0), 0) ? minutesToHHMM2(flights.reduce((acc, flight) => acc + (flight.delta ?? 0), 0)) : '00:00'}</strong>
                      </div>
                    </SpaceBetween>)
                  } columnDefinitions={
                    [
                      {
                        id: 'id',
                        header: 'ID',
                        cell: (item) => <FormField label={
                          item.idFlight}>
                        </FormField>,
                        maxWidth: 100,
                      },
                      {
                        id: 'date',
                        header: 'Fecha',
                        cell: (item) => <FormField label={item.departureDate}>
                          {`${differenceInDays(new Date(), parse(item.departureDate, "dd-MM-yyyy", new Date()))} D`}
                        </FormField>,
                        maxWidth: 100,
                      },
                      {
                        id: 'aircraft',
                        header: 'Material',
                        cell: (item) => (
                          // 🔑 FIX 2: Add key to the outermost component in the cell
                          <FormField key={`aircraft-formfield-${item.idFlight}`} label={item.registration}>
                            {item.groupTypeMaster} {item.idTowingTo && item.idTowingTo > 0 ? <Icon path={mdiTowTruck} size={1} /> : ''}
                          </FormField>
                        ),
                        maxWidth: 80,
                      },
                      {
                        id: 'flightType',
                        header: 'Tipo',
                        cell: (item) => item.flightType,
                        maxWidth: 100
                      },
                      {
                        id: 'duration',
                        header: 'Tiempo',
                        cell: (item) => minutesToHHMM2(item.delta ?? 0),
                        maxWidth: 80,
                      },
                    ]}

                />

              </Modal>

            )}
          </SpaceBetween>
        </>
      }
    />
  );
};
